import { t } from "@lingui/macro";
import {
  type BasicDataQuery,
  BillingEntitiesDataDocument,
  type BillingEntitiesDataQuery,
  type BillingEntitiesDataQueryVariables,
  type BrandsDataQuery,
  type BudgetCategory,
  BudgetItemTypeEnum,
  type BudgetPosition,
  CheckSequenceNumberExistDocument,
  type CheckSequenceNumberExistQuery,
  type CheckSequenceNumberExistQueryVariables,
  ConvertAmountDocument,
  type ConvertAmountQuery,
  type ConvertAmountQueryVariables,
  type CostCenterFragment,
  type CreateOrderMutation,
  type CreateRequestForInvoicingInput,
  type DocumentsDataQuery,
  ExchangeRateSource,
  GetExchangeRateDocument,
  type GetExchangeRateQuery,
  type GetExchangeRateQueryVariables,
  GetNewSequenceNumberDocument,
  type GetNewSequenceNumberQuery,
  type GetNewSequenceNumberQueryVariables,
  GetOutgoingInvoicesDocument,
  type GetOutgoingInvoicesQuery,
  type GetOutgoingInvoicesQueryVariables,
  ModelLockLockableTypeEnum,
  OrdersDocument,
  type OrdersQuery,
  type OrdersQueryVariables,
  OrdersWhereColumn,
  OutgoingInvoiceDocument,
  type OutgoingInvoiceQuery,
  type OutgoingInvoiceQueryVariables,
  OutgoingInvoiceTypeEnum,
  type PaginatorInfo,
  ProjectsDataDocument,
  type ProjectsDataQuery,
  type ProjectsDataQueryVariables,
  type ProjectsSimpleQuery,
  ProjectTypeEnum,
  type ProofOfPaymentFragment,
  type RequestForInvoicingStatusEnum,
  RequestForInvoicingTypeEnum,
  SequenceTypesEnum,
  SqlOperator,
  type UnusedPaidProformaInvoicesQuery,
  type UpdateOutgoingInvoiceInput,
  type UpdateOutgoingProformaInvoiceInput,
  type UpdateRequestForInvoicingInput,
} from "@src/__generated__/graphql";
import { CREDIT_NOTE_OUTGOING_INVOICE_QUERY_KEY } from "@src/components/modules/invoices/outgoing/listing/table/PopoverRowActions";
import type { IOption } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { client } from "@src/services/apollo-client";
import { type AppStore, appStore } from "@src/stores/AppStore";
import type { FilterModelType } from "@src/stores/FilterTemplatesStore";
import { EXCHANGE_RATE_DENOMINATION } from "@src/stores/forms/SettingsModalState/FinancialSettingsState";
import type { MediaModel } from "@src/utils/components/dropzone";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { can } from "@src/utils/components/permissions";
import { toApiDate } from "@src/utils/dates";
import {
  currency,
  HUNDRED_PERCENT,
  nominate,
  percent,
} from "@src/utils/formatters";
import { gt, lt } from "@src/utils/forms/validators";
import {
  default as map,
  default as mapToOptions,
} from "@src/utils/map-to-options";
import { toCostCentersOptions } from "@src/utils/map-to-options/cost-centers";
import { toTagOptions } from "@src/utils/map-to-options/tags";
import type { UserOption } from "@src/utils/map-to-options/users";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { NumberModel, PercentModel } from "@src/utils/models";
import { type DateRange, FormMode } from "@src/utils/types";
import { addDays } from "date-fns";
import { FieldState, FormState } from "formstate";
import { GraphQLError } from "graphql";
import {
  compact,
  groupBy,
  map as lodashMap,
  omit,
  orderBy,
  sumBy,
  uniqBy,
} from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";
import { assert } from "ts-essentials";
import {
  type GenerateFromOption,
  TimeAndMaterialsProjectCardStore,
} from "../components/Steps/BudgetsSplits/TimeAndMaterials/store";
import { Form, type TOurWorkBudgetSplitForm } from "../form";
import {
  type BillingEntities,
  type Brands,
  type ClientsSimple,
  isTOurWorkBudgetItem,
  type OutgoingInvoiceData,
  type ProformaInvoiceData,
  type ProformaInvoices,
  type Projects,
  type ProjectsSimple,
  type RequestForInvoicingData,
  type TExpenseBudgetItem,
  type TeamSimple,
  type TOurWorkBudgetItem,
  type UsersSimple,
  type VatRates,
  WithPdfModel,
} from "../models";
import {
  getSteps,
  type InvoiceItemVatGroup,
  type InvoiceStep,
  InvoiceStepIds,
  InvoiceTypeEnum,
  InvoiceTypeEnumFrom,
  InvoiceTypeEnumToRfiEnum,
} from ".";

type ExpenseBudgetItemSplit = {
  expense_budget_item_id?: string | null;
  amount?: number | null;
};

type OurWorkBudgetItemSplit = {
  our_work_budget_item_id?: string | null;
  amount?: number | null;
  amount_in_percent?: number | null;
};
type PrepaymentItemSplit = {
  brand_credit_account_id?: string | null;
  amount?: number | null;
};

export class InvoiceStore {
  @observable.ref type?: InvoiceTypeEnum;
  @observable.ref isRFI: boolean;
  @observable.ref currentStepIndex = 0;
  @observable hideAlreadyInvoiced = true;
  @observable showOnlyChecked = false;

  showQuantity: BooleanState;
  showAccountClassification: BooleanState;

  @observable.ref accountingAccountOptions: IOption[] = [];
  @observable.ref clientsSimple: ClientsSimple = [];
  @observable.ref usersSimple: UsersSimple = [];
  @observable.ref teamSimple: TeamSimple = [];

  @observable.ref vatRates: VatRates = [];
  @observable clientsUsersVatRatesLoading = false;

  @observable.ref selectedBrands: Brands = [];
  @observable selectedBrandsLoading = false;
  selectedBrandsRefetch?: () => any;

  @observable.ref billingEntities: BillingEntities = [];

  private billingEntitiesFetcher = new FetchHelper<
    BillingEntitiesDataQuery,
    BillingEntitiesDataQueryVariables
  >(BillingEntitiesDataDocument);

  @observable.ref _projectsSimple: ProjectsSimple = [];

  @observable.ref _proformaInvoices: ProformaInvoices = [];

  @observable.ref selectedProjects: Projects = [];
  @observable selectedProjectsLoading = false;
  selectedProjectsRefetch?: () => any;

  @observable ordersWithPdf: WithPdfModel[] = [];
  @observable otherDocumentsWithPdf: WithPdfModel[] = [];
  documentsRefetch?: () => any;

  @observable.ref invoice?:
    | RequestForInvoicingData
    | OutgoingInvoiceData
    | ProformaInvoiceData;
  @observable convertedAmount?: number;
  @observable invoiceMutationLoading = false;
  @observable showVat = false;
  @observable.ref skip_date_validations: boolean;
  @observable fetchingNewInvoiceNumber = false;

  appStore: AppStore;
  form: Form;
  timeAndMaterialsStore: TimeAndMaterialsProjectCardStore;

  exchangeRateFetcher = new FetchHelper<
    GetExchangeRateQuery,
    GetExchangeRateQueryVariables
  >(GetExchangeRateDocument);

  private workspaceExchangeRateFetcher = new FetchHelper<
    GetExchangeRateQuery,
    GetExchangeRateQueryVariables
  >(GetExchangeRateDocument);

  convertAmountFetcher = new FetchHelper<
    ConvertAmountQuery,
    ConvertAmountQueryVariables
  >(ConvertAmountDocument);

  private invoiceNoCheckFetcher = new FetchHelper<
    CheckSequenceNumberExistQuery,
    CheckSequenceNumberExistQueryVariables
  >(CheckSequenceNumberExistDocument);

  @observable.ref invoiceNoOptions: IOption[] = [];
  private invoiceNoFetcher = new FetchHelper<
    GetNewSequenceNumberQuery,
    GetNewSequenceNumberQueryVariables
  >(GetNewSequenceNumberDocument);

  private outgoingInvoicesFetcher = new FetchHelper<
    GetOutgoingInvoicesQuery,
    GetOutgoingInvoicesQueryVariables
  >(GetOutgoingInvoicesDocument);

  private outgoingInvoiceDetailFetcher = new FetchHelper<
    OutgoingInvoiceQuery,
    OutgoingInvoiceQueryVariables
  >(OutgoingInvoiceDocument);

  @observable.ref
  originalInvoiceOption: IOption[] | undefined;

  @observable.ref steps: InvoiceStep[];

  @observable orderOptions: IOption[] = [];
  orderFetcher = new FetchHelper<OrdersQuery, OrdersQueryVariables>(
    OrdersDocument,
  );

  @observable.ref invoicePaymentMethodOptions: IOption[] = [];
  ordersWhere = new Filters<OrdersWhereColumn>([
    new Filter({
      column: OrdersWhereColumn.ProjectId,
      value: "",
      options: [],
      operator: SqlOperator.In,
    }),
    new Filter({
      column: OrdersWhereColumn.BrandId,
      value: "",
      options: [],
      operator: SqlOperator.In,
    }),
  ]);

  @observable.ref costCenters: CostCenterFragment[] = [];

  constructor(
    appStore: AppStore,
    isRFI: boolean,
    type: InvoiceTypeEnum,
    public formMode: FormMode,
  ) {
    makeObservable(this);
    const steps = getSteps(type === InvoiceTypeEnum.CreditNote);
    this.steps = steps;
    this.appStore = appStore;
    this.timeAndMaterialsStore = new TimeAndMaterialsProjectCardStore(
      appStore,
      this,
    );
    this.isRFI = isRFI;
    this.type = type;
    this.form = new Form(this);
    this.form.currency_id.onChange(
      appStore.workspaceStore.settings!.currency.id,
    );
    this.showBudgetItem = this.showBudgetItem.bind(this);
    this.skip_date_validations =
      appStore.workspaceStore.settings?.can_skip_invoice_date_validations ??
      false;
    this.showQuantity = new BooleanState(
      appStore.workspaceStore.settings?.defaultShowUnitPriceOnInvoices,
    );
    this.showAccountClassification = new BooleanState(
      appStore.workspaceStore.settings?.defaultShowBillingAccountingOnInvoices,
    );

    appStore.tagStore.fetchTags();

    if (type !== InvoiceTypeEnum.CreditNote && formMode !== FormMode.CREATE)
      return;
    const outgoingInvoiceId =
      Router.query[CREDIT_NOTE_OUTGOING_INVOICE_QUERY_KEY];
    if (!outgoingInvoiceId || typeof outgoingInvoiceId !== "string") return;
    this.handleSelectOutgoingInvoice(outgoingInvoiceId);
  }

  @computed
  get invoiceSequenceType(): SequenceTypesEnum | null {
    if (this.type === InvoiceTypeEnum.Proforma)
      return SequenceTypesEnum.Proforma;
    if (this.type === InvoiceTypeEnum.CreditNote)
      return SequenceTypesEnum.CreditNote;
    if (!this.invoice || !("type" in this.invoice))
      return SequenceTypesEnum.OutgoingInvoice;
    return this.invoice.type === OutgoingInvoiceTypeEnum.ProformaPayment
      ? SequenceTypesEnum.ProformaPayment
      : SequenceTypesEnum.OutgoingInvoice;
  }

  @computed get invoice_id() {
    return this.invoice?.id;
  }

  @computed get invoice_no() {
    if (this.invoice && "invoice_no" in this.invoice) {
      return this.invoice.invoice_no;
    }
    return undefined;
  }

  @computed
  get noSequenceOptionsMessage(): string | undefined {
    const isProforma = this.type === InvoiceTypeEnum.Proforma;
    const date = isProforma
      ? this.form.issue_date.value
      : this.form.date_of_supply.value;

    if (!date && !this.form.workspace_company_id.value) {
      return t`Please select issue date and legal entity first.`;
    }

    return !date
      ? isProforma
        ? t`Please select issue date first.`
        : t`Please select date of supply first.`
      : !this.form.workspace_company_id.value
        ? t`Please select legal entity first`
        : undefined;
  }

  @action.bound
  handleSelectInvoiceNo(option: IOption, fetchInvoiceNo = true) {
    this.form.invoice_no.onChange(option.label);
    this.form.invoice_order_sequence_id.onChange(option.value);
    this.checkInvoiceNumberExist(option.label);
    if (!fetchInvoiceNo) return;
    const date =
      this.type === InvoiceTypeEnum.Proforma
        ? this.form.issue_date.value
        : this.form.date_of_supply.value;
    this.fetchInvoiceNo(
      date,
      this.form.workspace_company_id.value,
      this.invoiceSequenceType,
    );
  }

  @action.bound
  handleChangeInvoiceNo(invoiceNo: string) {
    this.form.invoice_no.onChange(invoiceNo);
    if (invoiceNo.length > 0) {
      this.checkInvoiceNumberExist(invoiceNo);
    } else {
      this.form.invoice_order_sequence_id.onChange("");
      this.form.invoice_order_sequence_id.validate();
    }
  }

  @action.bound
  handleSelectBillingEntity(value: string) {
    if (!this.showWorkspaceExchangeRate) {
      this.form.workspace_exchange_rate_source.onChange(
        ExchangeRateSource.Budget,
      );
    }

    this.form.billing_entity_id.onChange(value);

    const billingEntity = this.selectedBillingEntity;

    if (billingEntity) {
      this.form.workspace_company_id.onChange(
        billingEntity.workspaceCompany.id,
      );
      this.form.workspace_bank_account_id.onChange(
        billingEntity.workspaceBankAccount?.id,
      );
    }

    const currencyId = billingEntity?.defaultCurrency.id;

    if (currencyId && currencyId !== this.form.currency_id.value) {
      this.onSelectCurrency(currencyId);
    } else {
      this.fetchExchangeRates();
    }

    this.syncVatReverseChargeWithCountries();
  }

  @action.bound
  handleChangeCompany(companyId: string) {
    if (!this.showWorkspaceExchangeRate) {
      this.form.handleChangeWorkspaceExchangeRateSource(
        ExchangeRateSource.Budget,
      );
    }

    this.form.workspace_company_id.onChange(companyId);
    this.form.workspace_bank_account_id.onChange(
      this.appStore.workspaceStore.getDefaultCompanyBankAccount(companyId)?.id,
    );
    this.form.invoice_no.onChange(undefined);
    this.form.invoice_order_sequence_id.onChange("");

    const date =
      this.type === InvoiceTypeEnum.Proforma
        ? this.form.issue_date.value
        : this.form.date_of_supply.value;
    this.fetchInvoiceNo(date, companyId, this.invoiceSequenceType);
    this.syncVatReverseChargeWithCountries();
  }

  @computed
  get isInvoiceNoFetching() {
    return this.invoiceNoFetcher.isLoading.value;
  }

  @action.bound
  async fetchInvoiceNo(
    date: Date | string | undefined | null,
    workspaceCompanyId:
      | GetNewSequenceNumberQueryVariables["workspace_company_id"]
      | undefined
      | null,
    invoiceType: GetNewSequenceNumberQueryVariables["invoice_type"],
    preselectInvoiceNo = true,
  ) {
    if (!date || !workspaceCompanyId) return;
    const [data, error] = await this.invoiceNoFetcher.fetch({
      date: typeof date === "string" ? date : toApiDate(date),
      workspace_company_id: workspaceCompanyId,
      invoice_type: invoiceType,
      documentId: this.isRFI ? undefined : this.invoice_id,
    });

    if (error) return;

    this.invoiceNoOptions = data.getNewInvoiceNumber.map(
      ({ id, document_no }) => ({
        label: document_no,
        value: id,
      }),
    );

    if (!preselectInvoiceNo) return;
    if (data.getNewInvoiceNumber.length !== 1) return;
    this.handleSelectInvoiceNo(this.invoiceNoOptions[0], false);
  }

  @action.bound
  handleInvoiceAutocompletePopoverOpen() {
    const date =
      this.type === InvoiceTypeEnum.Proforma
        ? this.form.issue_date.value
        : this.form.date_of_supply.value;
    this.fetchInvoiceNo(
      date,
      this.form.workspace_company_id.value,
      this.invoiceSequenceType,
      false,
    );
  }

  @computed
  get checkingError() {
    return this.invoiceNoCheckFetcher.isLoading.value;
  }

  @action.bound
  async checkInvoiceNumberExist(invoiceNo: string) {
    const date =
      this.type === InvoiceTypeEnum.Proforma
        ? this.form.issue_date.value
        : this.form.date_of_supply.value;

    if (!date || !this.form.invoice_order_sequence_id.value) return;

    const [data, error] = await this.invoiceNoCheckFetcher.fetch({
      document_no: invoiceNo,
      workspace_company_id: this.form.workspace_company_id.value,
      invoice_type: this.invoiceSequenceType,
      document_id: this.isRFI ? undefined : this.invoice_id,
      date: toApiDate(date),
      invoice_order_sequence_id: this.form.invoice_order_sequence_id.value,
    });

    if (error instanceof GraphQLError) {
      this.form.invoice_no.setError(
        error?.message.replace("[GraphQL] ", "") ?? "",
      );
      return;
    }

    if (data?.checkInvoiceNumberExist === false) return;
    this.form.invoice_no.setError(t`Invoice number already exists.`);
  }

  @computed get old_invoice_alert() {
    if (this.invoice && "old_invoice_alert_while_editing" in this.invoice) {
      return this.invoice.old_invoice_alert_while_editing;
    }
    return undefined;
  }

  @computed get projectOverdue():
    | { finished: boolean; archived: boolean }
    | undefined {
    let finished = false;
    let archived = false;
    this.selectedProjects.forEach((project) => {
      if (project.finished_at) finished = true;
      if (project.archived_at) archived = true;
    });
    if (archived || finished) return { finished: finished, archived: archived };
    return undefined;
  }

  @computed get isLinkedToLockedTimeBilling(): boolean {
    if (!this.invoice) return false;
    if (!("projectTimeBilling" in this.invoice)) return false;
    return this.invoice.projectTimeBilling?.is_locked ?? false;
  }

  @computed get significantLoading() {
    return (
      this.selectedBrandsLoading ||
      this.selectedProjectsLoading ||
      this.invoiceMutationLoading ||
      this.timeAndMaterialsStore.savingFiltersTemplate ||
      this.isOutgoingInvoiceDetailLoading
    );
  }

  @computed get isBrandMultiSelect() {
    return this.type !== InvoiceTypeEnum.Proforma;
  }

  @computed get isTimeAndMaterialsSelected() {
    return this.selectedProjectType === ProjectTypeEnum.TimeMaterials;
  }

  @computed get isProjectMultiSelect() {
    return (
      this.type !== InvoiceTypeEnum.Proforma && !this.isTimeAndMaterialsSelected
    );
  }

  @computed get isFinancier() {
    return can("requestForInvoicing_accessAccountingAttributes");
  }

  @computed get isDueDateRequired() {
    return this.isFinancier || !this.isRFI;
  }

  @computed get isIssueDateRequired() {
    return this.isFinancier || !this.isRFI;
  }

  @computed get isDateOfSupplyRequired() {
    return this.type !== InvoiceTypeEnum.Proforma;
  }

  @computed get currentStep(): InvoiceStep {
    return this.steps[this.currentStepIndex];
  }

  @computed
  get isBudgetItemsStepVisible(): boolean {
    return (
      this.steps.findIndex((step) => step.id === InvoiceStepIds.BudgetItems) >
      -1
    );
  }

  @computed get hasCurrentStepErrors(): boolean {
    switch (this.currentStep.id) {
      case InvoiceStepIds.ProjectDetails:
        return this.form.steps[InvoiceStepIds.ProjectDetails].hasError;
      case InvoiceStepIds.BudgetItems:
        return this.form.steps[InvoiceStepIds.BudgetItems].hasError;
      case InvoiceStepIds.InvoiceDetails:
        return this.form.steps[InvoiceStepIds.InvoiceDetails].hasError;
      default:
        return false;
    }
  }

  /**
   * Group pdfFiles by relation to brand or project.
   */
  @computed get pdfFiles() {
    return [...this.ordersWithPdf, ...this.otherDocumentsWithPdf]
      .filter((i) => !!i.relatedTo)
      .reduce(
        (stack, i) => {
          assert(i.relatedTo);
          const group = i.relatedTo;

          if (stack[group]) {
            stack[group] = [...stack[group]!, ...i.pdfFiles];
          } else {
            stack[group] = i.pdfFiles;
          }

          return stack;
        },
        {} as { brand?: MediaModel[]; project?: MediaModel[] },
      );
  }

  @computed get selectedCurrency() {
    const selectedCurrencyId = this.form.currency_id.value;
    return (
      this.appStore.workspaceStore.currencies.find(
        (i) => i.id === selectedCurrencyId,
      ) || this.appStore.workspaceStore.settings!.currency
    );
  }

  get formattedInvoiceNo() {
    return this.invoice_no ? ` #${this.invoice_no}` : "";
  }

  @computed get isEdit(): boolean {
    return Boolean(this.invoice_id);
  }

  @computed get selectedBillingEntity() {
    return this.billingEntities.find(
      (i) => i.id === this.form.billing_entity_id.value,
    );
  }

  @computed get selectedBillingEntityCountryId() {
    return this.selectedBillingEntity?.country.id;
  }

  @computed
  get selectedLegalEntity() {
    const selectedEntityId = this.form.workspace_company_id.value;
    return this.appStore.workspaceStore.getCompany(selectedEntityId);
  }

  @computed get selectedLegalEntityCurrency() {
    return (
      this.appStore.workspaceStore.currencies.find(
        (currency) => currency.id === this.selectedLegalEntity?.currency_id,
      ) ?? this.appStore.workspaceStore.settings?.currency
    );
  }

  @computed get selectedLegalEntityCountryId() {
    const companyCountryId = this.selectedLegalEntity?.country?.id;

    if (companyCountryId) return companyCountryId;

    if (
      this.selectedBillingEntity?.workspaceCompany.id ===
      this.form.workspace_company_id.value
    ) {
      return this.selectedBillingEntity.country.id;
    }

    return undefined;
  }

  @computed get hasDifferentBillingAndLegalEntityCountry() {
    const billingCountryId = this.selectedBillingEntityCountryId;
    const legalCountryId = this.selectedLegalEntityCountryId;

    if (!billingCountryId || !legalCountryId) return false;

    return billingCountryId !== legalCountryId;
  }

  @computed get canEditBrand() {
    return this.isRFI || this.isFinancier;
  }

  @computed get canEditProfileDetails() {
    return this.isRFI || this.isFinancier;
  }

  @computed get projectsSimple() {
    return this._projectsSimple.filter((project) =>
      this.form.brand_ids.value.includes(project.brand_id),
    );
  }

  @computed get proformaInvoices(): ProformaInvoices {
    if (this.invoice && "proformaInvoices" in this.invoice) {
      return uniqBy(
        [...this._proformaInvoices, ...this.invoice.proformaInvoices],
        "id",
      );
    }
    return this._proformaInvoices;
  }

  // Options

  @computed get brandsOptions() {
    let clientsSimple = this.clientsSimple;
    if (this.form.client_id.value && this.isBrandMultiSelect) {
      clientsSimple = clientsSimple.filter(
        (i) => i.id === this.form.client_id.value,
      );
    }
    return map.clients.toOptions(clientsSimple);
  }

  @computed get teamOptions() {
    return this.teamSimple.map((i) => ({
      label: i.name,
      value: i.id,
    }));
  }

  @computed get selectedBrandsOptions() {
    return orderBy(
      this.selectedBrands.map((i) => ({
        label: i.name,
        value: i.id,
      })),
      ["label"],
      ["asc"],
    );
  }

  @computed get billingEntitiesOptions() {
    return map.billingEntities.toOptions(this.billingEntities);
  }

  /** Project options of selected brands */
  @computed get filteredProjectOptions() {
    return this.selectedBrands.map((i) => ({
      label: i.name,
      options: this.projectsSimple
        .filter((k) => k.brand_id === i.id)
        .filter(({ type }) =>
          this.isProjectMultiSelect && this.selectedProjectType
            ? type === this.selectedProjectType
            : true,
        )
        .map((k) => ({
          label: `${k.code} – ${k.title}`,
          value: k.id,
        })),
    }));
  }

  /** Selected projects as options */
  @computed get selectedProjectsOptions() {
    const projects = this.selectedProjects.filter(
      (i) => i.billable && i.status === "active",
    );
    return orderBy(
      projects.map((i) => ({
        id: i.id,
        label: `${i.code} – ${i.title}`,
        value: i.id,
        brandId: i.brand.id,
      })),
      ["label"],
      ["asc"],
    );
  }

  @computed get contactsOptions() {
    return this.selectedBrands.map((i) => ({
      label: i.name,
      options: mapToOptions.contacts(i.contacts),
    }));
  }

  @computed get ownersOptions(): UserOption[] {
    // We need add current owner because user can be deactivated and will not be included in query result
    const owner = this.invoice?.owner
      ? {
          id: this.invoice.owner.id,
          full_name: this.invoice.owner.full_name,
          name: this.invoice.owner.codename,
        }
      : null;

    return uniqBy(
      mapToOptions.users(compact([...this.usersSimple, owner])),
      "value",
    );
  }

  @computed get currenciesOptions() {
    return map.toOptions(this.appStore.workspaceStore.currencies, "code");
  }

  @computed get vatRatesOptions() {
    return this.vatRates
      .filter(
        ({ amount, deleted }) =>
          !deleted ||
          this.form.vat_percent_informative.value === amount.toString(),
      )
      .map((i) => ({
        label: percent.format(i.amount),
        value: i.amount.toString(),
      }));
  }

  @computed get selectedOrdersOptions() {
    const projectOrders = this.selectedProjects.map((i) => ({
      orders: i.ordersSimpleMap,
      projectId: i.id,
    }));

    return projectOrders.map((po) => {
      const selectedOrders = po.orders.filter((order) =>
        this.form.order_ids.$.includes(order.id),
      );
      return { orders: selectedOrders, id: po.projectId };
    });
  }

  @computed get creditAccountsOptions() {
    return this.selectedBrands.map((i) => ({
      label: i.name,
      options: (i.creditAccounts || []).map((j) => ({
        label: t`${j.title} (Balance: ${currency.formatByWorkspace(j.stats.remaining_credit)})`,
        value: j.id,
      })),
    }));
  }

  @computed get creditAccountsOptionsWithNew() {
    return [
      {
        label: t`New credit account`,
        value: `new`,
      },
      ...this.creditAccountsOptions,
    ];
  }

  @computed get proformaInvoicesOptions() {
    let proformaInvoices = this.proformaInvoices;

    // If we are editing OI and OI has proformaInvoices we need to add them to options because API return only proformaInvoices with some unused amount
    if (
      this.type === InvoiceTypeEnum.Outgoing &&
      this.invoice &&
      "proformaInvoicesPivot" in this.invoice
    ) {
      const invoiceProformaInvoices = this.invoice.proformaInvoicesPivot.map(
        (item) => item.invoice,
      );

      // merge and remove duplicates
      proformaInvoices = uniqBy(
        [...invoiceProformaInvoices, ...proformaInvoices],
        (invoice) => invoice.id,
      );

      // add amount we use from proforma to unused amount
      proformaInvoices = proformaInvoices.map((invoice) => {
        if (this.invoice && "proformaInvoicesPivot" in this.invoice) {
          const proformaInvoicePivot = this.invoice.proformaInvoicesPivot.find(
            (item) => item.invoice.id === invoice.id,
          );
          if (proformaInvoicePivot?.amount_in_currency) {
            invoice.stats.unused_amount_in_currency =
              invoice.stats.unused_amount_in_currency +
              proformaInvoicePivot.amount_in_currency;
          }
        }
        return invoice;
      });
    }

    return proformaInvoices.map((i) => ({
      label: `#${i.invoice_no} (${t`unused`} ${currency.formatByCurrency(
        i.stats.unused_amount_in_currency,
        i.currency,
      )} / ${currency.formatByCurrency(i.stats.total_amount_with_vat_in_currency, i.currency)})`,
      value: i.id,
    }));
  }

  @computed get sumInvoiceItems() {
    return this.form.invoice_items.$.reduce((prev, { $ }) => {
      return prev + $.amount_in_currency.value.denominatedValue;
    }, 0);
  }

  @computed
  get sumInvoiceItemsVat() {
    return this.form.invoice_items.$.reduce((prev, { $ }) => {
      return (
        prev +
        new PercentModel(
          Number($.vat_percent_informative.value ?? "0"),
        ).percentOf($.amount_in_currency.value.denominatedValue)
      );
    }, 0);
  }

  @computed
  get sumInvoiceItemsVatByPercent(): InvoiceItemVatGroup[] {
    const groups = new Map<number, number>();

    this.form.invoice_items.$.forEach(({ $ }) => {
      const vatPercent = Number($.vat_percent_informative.value ?? "0");
      const vatAmount = new PercentModel(vatPercent).percentOf(
        $.amount_in_currency.value.denominatedValue,
      );
      groups.set(vatPercent, (groups.get(vatPercent) ?? 0) + vatAmount);
    });

    const vats: InvoiceItemVatGroup[] = [];

    for (const [vatPercent, vatAmount] of groups.entries()) {
      vats.push({
        vatPercent,
        vatAmount,
        vatPercentFormatted: percent.format(vatPercent),
      });
    }

    return vats.toSorted((a, b) => a.vatPercent - b.vatPercent);
  }

  @computed get amountFromProofOfPayments() {
    return this.sumProformaInvoices;
  }

  @computed get sumProformaInvoices() {
    return this.paymentReceipts
      .filter((i) => this.form.payment_receipt_ids.value.includes(i.id))
      .reduce((prev, { stats }) => prev + stats.total_amount_in_currency, 0);
  }

  @computed get sumInvoiceItemsTotal() {
    return this.sumInvoiceItems + this.sumInvoiceItemsVat;
  }

  @computed
  get remaingingAmountWithVat() {
    const remaining = this.sumInvoiceItems - this.amountFromProofOfPayments;
    const remainingRatio = remaining / this.sumInvoiceItems;
    const remainingVat = this.sumInvoiceItemsVat * remainingRatio;

    return remaining + remainingVat;
  }

  @computed get vatPercent() {
    return PercentModel.fromRawValue(this.form.vat_percent_informative.$ ?? 0);
  }

  @computed get convertedAmountRequired() {
    return (
      this.selectedCurrency.id !==
      this.appStore.workspaceStore.settings?.currency.id
    );
  }

  @computed get amountForSplits() {
    if (this.convertedAmountRequired) {
      return this.convertedAmount ?? 0;
    }

    return this.sumInvoiceItems;
  }

  @computed
  get convertedAmountUsingInvoiceExchangeRate(): number {
    if (!this.showInvoiceExchangeRate) return 0;
    const rate = this.form.exchange_rate.value.value ?? 1;
    return this.sumInvoiceItems / rate;
  }

  @computed get sumActualSplits() {
    const sum =
      (this.isTimeAndMaterialsSelected
        ? this.timeAndMaterialsStore.totalToInvoice
        : this.sumTotalSplits) - this.amountForSplits;
    /**
     * Converting currency issue workaround
     * @see https://brackets.atlassian.net/browse/ALLF-4073
     */
    if (this.convertedAmountRequired) {
      return Math.abs(sum) < 90 ? 0 : sum;
    }
    return sum;
  }

  @computed get sumTotalSplits() {
    return this.sumBudgetsSplits + this.sumPrepaymentSplits;
  }

  @computed get sumBudgetsSplits() {
    return this.sumOurWorkBudgetsSplits + this.sumExpenseBudgetsSplits;
  }

  @computed get sumOurWorkBudgetsSplits() {
    return sumBy<TOurWorkBudgetSplitForm>(
      Array.from(this.form.our_work_budget_splits.$.values()),
      (i) => i.$.amount.value.denominatedValue,
    );
  }

  @computed get sumExpenseBudgetsSplits() {
    return sumBy(
      Array.from(this.form.expense_budget_splits.$.values()),
      (i) => i.$.amount.value.denominatedValue,
    );
  }

  @computed get ourWorkBudgetItems() {
    return compact(
      this.selectedProjects.flatMap((project) =>
        project.ourWorkStructured.flatMap(
          (structured) => structured.budgetItems,
        ),
      ),
    );
  }

  @computed get expenseBudgetItems() {
    return compact(
      this.selectedProjects.flatMap((project) =>
        project.expensesWithoutOtherStructured.flatMap(
          (structured) => structured.budgetItems,
        ),
      ),
    );
  }

  /**
    Yes, I know... Unfortunately, I don't think we would be able to achieve
    the same result without a massive rewrite of the whole invoice
    which we don't have time for right now.
  */
  @computed get sumExpenseBudgetSplitsByCategory(): Record<string, number> {
    const result: Record<string, number> = {};
    const splitIds: string[] = Array.from(
      this.form.expense_budget_splits.$.keys(),
    );
    lodashMap(
      groupBy(
        this.selectedProjects.flatMap((project) =>
          project.expensesWithoutOtherStructured.map((item) => ({
            name: item.name,
            sum: sumBy(
              item.budgetItems.filter((budgetItem) =>
                splitIds.includes(budgetItem.id),
              ),
              (item) => {
                const split = this.form.expense_budget_splits.$.get(item.id);
                return split?.$.amount.value.denominatedValue || 0;
              },
            ),
          })),
        ),
        (item) => item.name,
      ),
      (items, name) => {
        result[name as string] = sumBy(items, (item) => item.sum);
      },
    );

    return result;
  }

  @computed get sumPrepaymentSplits() {
    return sumBy(
      this.form.prepayment_splits.$,
      (p) => p.$.amount.value.denominatedValue,
    );
  }

  @computed get selectedMultipleTeams() {
    return this.form.team_ids.value.length > 1;
  }

  @computed
  get showInvoiceExchangeRate(): boolean {
    if (!this.selectedLegalEntity) return false;

    const invoiceCurrency = this.selectedCurrency.id;
    const legalCurrency = this.selectedLegalEntity.currency_id;
    const billingCurrency =
      this.selectedBillingEntity?.defaultCurrency.id ??
      this.appStore.workspaceStore.settings?.currency.id;

    if (
      invoiceCurrency === legalCurrency &&
      invoiceCurrency === billingCurrency
    ) {
      return false;
    }

    if (this.form.exchange_rate.value.value !== 1) return true;

    return !(
      invoiceCurrency === legalCurrency && invoiceCurrency === billingCurrency
    );
  }

  @computed
  get showWorkspaceExchangeRate(): boolean {
    if (this.form.workspace_exchange_rate.value.value !== 1) return true;

    return (
      this.appStore.workspaceStore.settings?.currency.id !==
      this.selectedCurrency.id
    );
  }

  @computed
  get needsExchangeRate() {
    return this.showInvoiceExchangeRate || this.showWorkspaceExchangeRate;
  }

  @computed
  get exchangeRateUnset(): boolean {
    return (
      this.showInvoiceExchangeRate && this.form.exchange_rate.$.value === 1
    );
  }

  @computed
  get workspaceExchangeRateUnset(): boolean {
    return (
      this.showWorkspaceExchangeRate &&
      this.form.workspace_exchange_rate.$.value === 1
    );
  }

  @computed
  get showExchangeRateUnsetWarning(): boolean {
    return (
      (this.showInvoiceExchangeRate && this.exchangeRateUnset) ||
      this.workspaceExchangeRateUnset
    );
  }

  @computed get selectedProjectType(): ProjectTypeEnum | undefined {
    const firstSelectedProject = this._projectsSimple.find(
      ({ id }) => this.form.project_ids.$?.[0] === id,
    );

    return firstSelectedProject?.type;
  }

  @computed get errorFields() {
    const errorFields: Array<FormState<any> | FieldState<any>> = [];

    for (const form of this.form.form.$) {
      if (!form.hasError) continue;
      const fieldCount = form.$.length;

      for (let i = 0; i < fieldCount; i++) {
        const field = form.$[i];

        if (!field.hasError) continue;
        errorFields.push(field);
      }
    }

    return errorFields;
  }

  @computed get dateErrorFields() {
    return this.errorFields.filter(({ $ }) => $ instanceof Date);
  }

  @computed get dateErrorsOnly() {
    return this.dateErrorFields.length === this.errorFields.length;
  }

  @action.bound onSelectCurrency(value: string) {
    this.form.currency_id.onChange(value);

    const matchingCurrency = this.appStore.workspaceStore.currencies.find(
      (currency) => currency.id === value,
    );

    if (
      this.showInvoiceExchangeRate &&
      matchingCurrency?.exchange_rate === null
    ) {
      this.form.exchange_rate_source.onChange(ExchangeRateSource.Auto);
    }

    if (
      this.showWorkspaceExchangeRate &&
      matchingCurrency?.exchange_rate === null
    ) {
      this.form.handleChangeWorkspaceExchangeRateSource(
        ExchangeRateSource.Budget,
      );
    }

    this.form.resetExchangeRate();
    this.form.syncInvoiceItemsWithCurrency();
    this.form.handleChangeWorkspaceExchangeRate("1");

    this.fetchExchangeRates();
  }

  @action setInvoiceMutationLoading = (loading: boolean) => {
    this.invoiceMutationLoading = loading;
  };

  @action.bound toggleHideAlreadyInvoiced(val?: boolean) {
    this.hideAlreadyInvoiced = val ?? !this.hideAlreadyInvoiced;
  }

  @action.bound toggleShowOnlyChecked(val?: boolean) {
    this.showOnlyChecked = val ?? !this.showOnlyChecked;
  }

  @action setConvertedAmount(amount?: number) {
    this.convertedAmount = amount;
  }

  @action.bound
  setSteps(isCreditNote: boolean) {
    this.steps = getSteps(isCreditNote);
  }

  @action.bound
  setCurrentStepIndex(index: number) {
    this.currentStepIndex = index;
  }

  @computed
  get hasNextStep(): boolean {
    return this.currentStepIndex < this.steps.length - 1;
  }

  @computed
  get hasPrevStep(): boolean {
    return this.currentStepIndex > 0;
  }

  @action.bound
  goToNextStep() {
    if (!this.hasNextStep) return;
    this.goToStep(this.currentStepIndex + 1);
  }

  @action.bound
  goToPrevStep = () => {
    if (!this.hasPrevStep) return;
    this.goToStep(this.currentStepIndex - 1);
  };

  @action.bound
  goToStep(index: number) {
    // We don't need to validate if a user is going to previous steps
    if (index < this.currentStepIndex) {
      return this.setCurrentStepIndex(index);
    }

    // Validate when a user is going to next steps
    if (this.type === InvoiceTypeEnum.Proforma) {
      return this.form.steps[InvoiceStepIds.ProjectDetails]
        .validate()
        .then((resp) => {
          if (!resp.hasError) this.setCurrentStepIndex(index);
        });
    }

    const step = this.steps[index];
    switch (step.id) {
      case InvoiceStepIds.BudgetItems:
        return this.form.steps[InvoiceStepIds.ProjectDetails]
          .validate()
          .then((resp) => {
            if (!resp.hasError) this.setCurrentStepIndex(index);
          });
      case InvoiceStepIds.InvoiceDetails:
        return this.setCurrentStepIndex(index);
      default:
        break;
    }
  }

  @action.bound calculateVAT(): void {
    if (this.form.vat_reverse_charge.value) return;

    const proformaVat = this.proformaInvoices[0]?.vat_percent_informative;

    if (this.sumProformaInvoices && proformaVat) {
      this.form.vat_percent_informative.onChange(proformaVat.toString());
    }
  }

  async validateAllSteps(
    ignoreInvoiceNoErrors?: boolean,
  ): Promise<{ hasError: boolean }> {
    if (
      this.steps.length === 3 &&
      this.selectedProjectType !== ProjectTypeEnum.TimeMaterials
    ) {
      const splitsMatchSumOnInvoice = () => {
        return this.sumActualSplits === 0
          ? null
          : t`Sum of splits must match sum of invoice items.`;
      };

      if (this.type !== InvoiceTypeEnum.Proforma) {
        const secondStep = await this.form.steps[InvoiceStepIds.BudgetItems]
          .validators(splitsMatchSumOnInvoice)
          .validate();

        if (secondStep.hasError) {
          this.setCurrentStepIndex(1);
          return { hasError: true };
        }
      }
    }

    const thirdStep = this.form.steps[InvoiceStepIds.InvoiceDetails];
    await thirdStep.validate();

    if (this.skip_date_validations) {
      this.form.due_date.error = undefined;
      this.form.issue_date.error = undefined;
      this.form.date_of_supply.error = undefined;
    }

    if (ignoreInvoiceNoErrors) {
      this.form.invoice_no.error = undefined;
      this.form.invoice_order_sequence_id.error = undefined;
    }

    if (thirdStep.hasError) {
      this.setCurrentStepIndex(2);
      return { hasError: true };
    }

    const firstStep =
      await this.form.steps[InvoiceStepIds.ProjectDetails].validate();
    if (firstStep.hasError) {
      this.setCurrentStepIndex(0);
      return { hasError: true };
    }

    return { hasError: false };
  }

  async validateAllStepsAsDraft(): Promise<{ hasError: boolean }> {
    if (this.steps.length === 3) {
      const secondStep = await this.form.prepayment_splits.validate();
      if (secondStep.hasError) {
        this.setCurrentStepIndex(1);
        return { hasError: true };
      }
    }

    const thirdStep = {
      exchangeRate: await this.form.exchange_rate.validate(),
    };
    if (thirdStep.exchangeRate.hasError) {
      this.setCurrentStepIndex(2);
      return { hasError: true };
    }

    const firstStep =
      await this.form.steps[InvoiceStepIds.ProjectDetails].validate();
    if (firstStep.hasError) {
      this.setCurrentStepIndex(0);
      return { hasError: true };
    }

    return { hasError: false };
  }

  @action setDependencies(query: BasicDataQuery) {
    this.clientsSimple = query.clientsSimpleMap;
    this.usersSimple = query.userSimpleMap;
    this.teamSimple = query.teamSimpleMap;
    this.vatRates = query.vatRates || [];
    this.accountingAccountOptions = mapToOptions.accountingAccounts(
      query.accountingAccountsSimpleMap,
    );
    this.costCenters = query.accountingCentres;
    this.invoicePaymentMethodOptions = mapToOptions.toOptions(
      query.invoicePaymentMethods,
      "name",
    );
  }

  generateCostCenterOptions(selectedValue: string | undefined) {
    return toCostCentersOptions(this.costCenters).filter(
      (option) => !option.isDisabled || option.value === selectedValue,
    );
  }

  @action setClientsUsersVatRatesLoading(loading: boolean) {
    this.clientsUsersVatRatesLoading = loading;
  }

  @action setProjectsSimple(query: ProjectsSimpleQuery) {
    this._projectsSimple = query.projectsSimpleMap;
  }

  @action setSelectedBrands(query: BrandsDataQuery) {
    this.selectedBrands = query.brands?.data || [];
    this.presetBillingEntity();
    this.presetTeam();

    if (!this.form.workspace_company_id.value) {
      if (query.brands?.data?.[0]?.client.workspaceCompany.id) {
        this.form.workspace_company_id.onChange(
          query.brands.data[0].client.workspaceCompany.id,
        );
      }
    }

    if (
      query.brands?.data.some(
        ({ client }) =>
          client.workspaceCompany.vat_no &&
          client.workspaceCompany.vat_no.length > 0,
      )
    ) {
      this.showVat = true;
    }

    const textOnInvoice =
      query.brands?.data[0]?.client.workspaceCompany.invoicePdfSettings
        ?.text_on_invoice;
    if (textOnInvoice && !this.invoice) {
      this.form.text_on_invoice.onChange(textOnInvoice);
    }

    if (this.formMode !== FormMode.EDIT) {
      this.syncVatReverseChargeWithCountries();
    }
  }

  @action setSelectedBrandsLoading(loading: boolean) {
    this.selectedBrandsLoading = loading;
  }

  @action setBillingEntities(query: BillingEntitiesDataQuery) {
    this.billingEntities = query.client?.billingEntities || [];

    if (!this.form.billing_entity_id.value) return;

    this.syncVatReverseChargeWithCountries({
      defaultWhenDifferent: this.formMode !== FormMode.EDIT,
      applyDefaultVatWhenSame: this.formMode !== FormMode.EDIT,
    });
  }

  @action.bound
  async fetchBillingEntities(
    clientId: string = this.form.client_id.value,
  ): Promise<void> {
    if (!clientId) {
      this.billingEntities = [];
      return;
    }

    const [data, error] = await this.billingEntitiesFetcher.fetch({
      client_id: clientId,
    });

    if (error || !data) return;

    this.setBillingEntities(data);
  }

  public canSkipPosition(position: BudgetPosition): boolean {
    let skip = true;

    Object.entries(position).forEach(([key, value]) => {
      if (
        key === "title" ||
        key === "__typename" ||
        key === "time_tracking_work_type_id" ||
        key === "our_work_budget_item_id" ||
        key === "quantity"
      )
        return;
      if (!value) return;
      if (value === 0) return;
      skip = false;
    });

    return skip;
  }

  @action setSelectedProjects(
    query: ProjectsDataQuery,
    skipTimeAndMaterialsInvoiceDataFetch?: boolean,
  ) {
    this.selectedProjects = query.projects?.data || [];

    if (
      query.projects?.data.length &&
      query.projects?.data[0]?.type === ProjectTypeEnum.TimeMaterials
    ) {
      this.timeAndMaterialsStore.projectId = query.projects.data[0].id;
      this.timeAndMaterialsStore.fetchFilterOptions();
      !skipTimeAndMaterialsInvoiceDataFetch &&
        this.timeAndMaterialsStore.fetchInvoiceData(query.projects.data[0].id);
    } else {
      const ourWorkBudgetSplits = this.form.our_work_budget_splits.$;

      for (const { ourWorkStructured } of this.selectedProjects) {
        for (const { budgetItems } of ourWorkStructured) {
          for (const { id, budget_positions } of budgetItems) {
            for (const [splitId, splitForm] of Array.from(
              ourWorkBudgetSplits,
            )) {
              if (splitId !== id) continue;

              for (const position of budget_positions) {
                const invoicedAmount = position?.invoiced_amount ?? 0;
                const isInvoiced = invoicedAmount > 0;

                if (this.canSkipPosition(position)) continue;

                splitForm.$.invoiced_positions.$.set(
                  position,

                  new FormState({
                    isInvoiced: new FieldState(isInvoiced),
                    amount: new FieldState(
                      new NumberModel(
                        invoicedAmount,
                        this.appStore.workspaceStore.settings?.currency
                          .denomination!,
                      ),
                    ),
                    unit_amount: new FieldState(
                      new NumberModel(
                        position.uninvoiced_amount ?? 0,
                        this.appStore.workspaceStore.settings?.currency
                          .denomination!,
                      ),
                    ),
                    quantity: new FieldState(
                      position.quantity?.toString() ?? "1",
                    ),
                  })
                    .compose()
                    .validators(($) => {
                      if (!$.isInvoiced.value) return;
                      const rule =
                        this.type === InvoiceTypeEnum.CreditNote
                          ? lt(0)
                          : gt(0);
                      return rule($.amount.$);
                    }),
                );
              }
            }
          }
        }
      }
    }

    this.filterSelectedSplits();
    this.presetContact();
    this.presetOwner();
  }

  @action setSelectedProjectsLoading(loading: boolean) {
    this.selectedProjectsLoading = loading;
  }

  @action setDocumentsWithPdf(data: DocumentsDataQuery) {
    this.ordersWithPdf = data.ordersWithPdf.map(
      (i) => new WithPdfModel(i, this),
    );
    this.otherDocumentsWithPdf = data.otherDocumentsWithPdf.map(
      (i) => new WithPdfModel(i, this),
    );
  }

  @action.bound async presetTimeAndMaterials(
    payload: OutgoingInvoiceData | RequestForInvoicingData,
    type: FilterModelType,
  ) {
    if (
      payload.projects.every(
        (project) => project.type !== ProjectTypeEnum.TimeMaterials,
      )
    )
      return;

    this.timeAndMaterialsStore.projectId =
      payload.timeMaterialsInvoiceData?.project_id;
    this.timeAndMaterialsStore.setInvoiceData(payload.timeMaterialsInvoiceData);

    const template = await this.appStore.filterTemplatesStore.fetchTemplate(
      payload.id,
      type,
    );

    if (!template) return;

    this.timeAndMaterialsStore.setFiltersFromTemplateParams(
      template[template.length - 1],
      () => {
        this.timeAndMaterialsStore.workTableState.handleSelectRows(
          payload.timeMaterialsInvoiceData?.ids ?? [],
        );
        this.timeAndMaterialsStore.workTableState.setAmountsFromSplits(
          payload.ourWorkBudgetSplits,
        );

        this.timeAndMaterialsStore.expenseTableState.setCommissionData(
          payload.timeMaterialsInvoiceData?.commission_data ?? [],
        );
        this.timeAndMaterialsStore.expenseTableState.handleSelectRows(
          payload.timeMaterialsInvoiceData?.expense_ids ?? [],
        );
        this.timeAndMaterialsStore.expenseTableState.setAmountsFromSplits(
          payload.expenseBudgetSplits,
        );
      },
    );
  }

  @action setProformaInvoices(query: UnusedPaidProformaInvoicesQuery) {
    this._proformaInvoices = query.unusedPaidOutgoingProformaInvoices;
  }

  @action.bound setDataFromRFI(payload: RequestForInvoicingData) {
    this.isRFI = true;
    this.type = InvoiceTypeEnumFrom(payload.type);
    this.setSteps(payload.type === RequestForInvoicingTypeEnum.CreditNote);
    this.invoice = payload;
    this.hideAlreadyInvoiced = true;
    this.showOnlyChecked = false;
    this.fillForm(payload);
    this.presetTimeAndMaterials(payload, "rfi");
    if (!("credit_note_outgoing_invoice_id" in payload)) return;
  }

  @action setDataFromOI(payload: OutgoingInvoiceData) {
    this.isRFI = false;
    this.type = InvoiceTypeEnumFrom(payload.type);
    this.invoice = payload;
    this.hideAlreadyInvoiced = false;
    this.showOnlyChecked = true;
    this.fillForm(payload);
    this.presetTimeAndMaterials(payload, "oi");
    if (!("credit_note_outgoing_invoice_id" in payload)) return;
  }

  @action.bound setDataFromProforma(payload: ProformaInvoiceData) {
    this.isRFI = false;
    this.setSteps(false);
    this.type = InvoiceTypeEnum.Proforma;
    this.invoice = payload;
    this.fillForm(payload);
  }

  /**
   * @param amount Amount to convert
   * @param exchangeRate If nothing is passed, uses exchange rate from form.workspace_exchange_rate
   * Converts amount by passed exchangeRate
   * @returns amount multiplied by exchangeRate
   */
  @action.bound convertAmount(
    amount?: number | null,
    exchangeRate?: number,
  ): number {
    return (
      (amount ?? 0) *
      (exchangeRate ?? this.form.workspace_exchange_rate.value.value)
    );
  }

  /**
   * @param amount Amount to format
   * @returns Amount formatByCurrency with selectedCurrency
   */
  @action.bound formatBySelectedCurrency(amount = 0): string {
    return currency.formatByCurrency(amount, this.selectedCurrency);
  }

  /**
   * @param amount Amount to convert and format
   * @returns Amount multiplied by exchange_rate formatted by selectedCurrency
   */
  @action.bound convertAndFormat(amount = 0): string {
    return this.formatBySelectedCurrency(
      this.convertAmount(amount, this.form.workspace_exchange_rate.value.value),
    );
  }

  duplicate(
    payload:
      | RequestForInvoicingData
      | OutgoingInvoiceData
      | ProformaInvoiceData,
  ) {
    this.fillForm(
      omit(payload, "date_of_supply", "issue_date", "due_date") as
        | RequestForInvoicingData
        | OutgoingInvoiceData
        | ProformaInvoiceData,
    );
  }

  @action async fillForm(
    payload:
      | RequestForInvoicingData
      | OutgoingInvoiceData
      | ProformaInvoiceData,
    forCreditNode = false,
  ) {
    const { form } = this;
    const isTimeAndMaterials = payload.projects.every(
      (project) =>
        "type" in project && project.type === ProjectTypeEnum.TimeMaterials,
    );
    const hasTimeAndMaterialsData =
      "timeMaterialsInvoiceData" in payload &&
      payload.timeMaterialsInvoiceData !== null;

    form.reset();
    this.billingEntities = payload.client.billingEntities;
    form.client_id.onChange(payload.client.id);
    const brandIds = payload.brands.map((i) => i.id);
    form.brand_ids.onChange(brandIds);
    this.setOrdersBrandFilter(brandIds);
    form.billing_entity_id.onChange(payload.billingEntity?.id || "");
    const projectIds = payload.projects.map((i) => i.id);
    form.project_ids.onChange(projectIds);
    this.setOrdersProjectFilter(projectIds);

    if (
      "credit_note_outgoing_invoice_id" in payload &&
      payload.credit_note_outgoing_invoice_id
    ) {
      this.form.credit_note_outgoing_invoice_id.onChange(
        payload.credit_note_outgoing_invoice_id,
      );
      this.fetchOriginalInvoiceOption(payload.credit_note_outgoing_invoice_id);
    }

    const workspaceDenomination =
      this.appStore.workspaceStore.settings!.currency.denomination;

    if ("ourWorkBudgetSplits" in payload && !isTimeAndMaterials) {
      const budgetPositionsByBudgetId: Record<string, BudgetPosition[]> =
        groupBy(payload.budget_positions, "our_work_budget_item_id");

      payload.ourWorkBudgetSplits?.forEach((i: OurWorkBudgetItemSplit) => {
        if (i.our_work_budget_item_id) {
          const budgetPositions =
            budgetPositionsByBudgetId[i.our_work_budget_item_id] || [];
          const categoryId = payload.projects
            .flatMap(({ ourWorkStructured }) => ourWorkStructured)
            .flatMap(({ budgetItems }) => budgetItems)
            .find((budgetItem) => budgetItem.id === i.our_work_budget_item_id)
            ?.category.id;

          if (!categoryId) {
            this.appStore.UIStore.toast({
              status: "error",
              title: t`Can't find category ID. Please try again. owbi`,
            });
            return;
          }

          const amountInPercent = i.amount_in_percent ?? 0;

          form.addOurWorkBudgetSplit(
            i.our_work_budget_item_id,
            new NumberModel(
              i.amount ? (forCreditNode ? -i.amount : i.amount) : 0,
              workspaceDenomination,
            ),
            budgetPositions,
            categoryId,
            new NumberModel(amountInPercent, 2),
          );
        }
      });
    }

    if ("expenseBudgetSplits" in payload && !isTimeAndMaterials) {
      payload.expenseBudgetSplits?.forEach((i: ExpenseBudgetItemSplit) => {
        if (i.expense_budget_item_id) {
          const categoryId = payload.projects
            .flatMap(
              ({ expensesWithoutOtherStructured }) =>
                expensesWithoutOtherStructured,
            )
            .flatMap(({ budgetItems }) => budgetItems)
            .find((budgetItem) => budgetItem.id === i.expense_budget_item_id)
            ?.category.id;

          if (!categoryId) {
            this.appStore.UIStore.toast({
              status: "error",
              title: t`Can't find category ID. Please try again. expense`,
            });
            return;
          }

          form.addExpenseBudgetSplit(
            i.expense_budget_item_id,
            new NumberModel(
              i.amount ? (forCreditNode ? -i.amount : i.amount) : 0,
              workspaceDenomination,
            ),
            categoryId,
          );
        }
      });
    }

    if ("prepaymentSplits" in payload) {
      payload.prepaymentSplits?.forEach((i: PrepaymentItemSplit) => {
        if (i.brand_credit_account_id) {
          form.addPrepaymentSplit(
            i.brand_credit_account_id,
            "",
            new NumberModel(
              i.amount ? (forCreditNode ? -i.amount : i.amount) : 0,
              workspaceDenomination,
            ),
          );
        }
      });
    }

    await this.fetchProjectsData(isTimeAndMaterials && hasTimeAndMaterialsData);
    form.contact_id.onChange(payload.contact?.id || "");
    form.team_ids.onChange(payload.teams.map((team) => team.id));
    form.currency_id.onChange(payload.currency.id);
    form.media_ids.onChange(payload.filesToAttach.map((i) => i.public_id));
    form.owner_id.onChange(payload.owner?.id || "");
    form.exchange_rate.onChange(
      form.exchange_rate.value.setDenomValue(payload.exchange_rate),
    );
    form.exchange_rate_source.onChange(payload.exchange_rate_source);
    form.workspace_exchange_rate.onChange(
      form.workspace_exchange_rate.value.setDenomValue(
        payload.workspace_exchange_rate,
      ),
    );
    form.workspace_exchange_rate_source.onChange(
      payload.workspace_exchange_rate_source,
    );
    form.workspace_company_id.onChange(payload.workspace_company_id);
    form.workspace_bank_account_id.onChange(
      payload.workspace_bank_account_id ?? undefined,
    );

    if (this.showQuantity.value !== payload.show_unit_price) {
      this.showQuantity.toggle();
    }
    if (
      this.showAccountClassification.value !== payload.show_billing_accounting
    ) {
      this.showAccountClassification.toggle();
    }

    if ("orders" in payload) {
      form.order_ids.onChange(payload.orders.map((i) => i.id));
      for (const order of payload.orders) {
        this.addOrderOption(order);
      }
    }
    if (
      "proformaInvoicesPivot" in payload &&
      payload.proformaInvoicesPivot.length > 0
    ) {
      form.outgoing_proforma_invoice_ids.onChange(
        payload.proformaInvoicesPivot.map((i) => i.invoice.id),
      );
    }
    if ("proformaInvoices" in payload && payload.proformaInvoices.length > 0) {
      form.outgoing_proforma_invoice_ids.onChange(
        payload.proformaInvoices.map((i) => i.id),
      );
    }
    if (typeof payload.vat_percent_informative === "number") {
      form.vat_percent_informative.onChange(
        payload.vat_percent_informative.toString(),
      );
    }
    if ("vat_reverse_charge" in payload) {
      form.vat_reverse_charge.onChange(payload.vat_reverse_charge);
    }
    this.syncVatReverseChargeWithCountries({
      defaultWhenDifferent: false,
      applyDefaultVatWhenSame: false,
    });
    if ("date_of_supply" in payload && payload.date_of_supply) {
      form.date_of_supply.onChange(new Date(payload.date_of_supply));
    }
    if (payload.issue_date) {
      form.issue_date.onChange(new Date(payload.issue_date));
    }
    if (payload.due_date) {
      form.due_date.onChange(new Date(payload.due_date));
    }
    if (payload.note) {
      form.note.onChange(payload.note);
    }
    if (payload.internal_note) {
      form.internal_note.onChange(payload.internal_note);
    }
    if (payload.text_on_invoice) {
      form.text_on_invoice.onChange(payload.text_on_invoice);
    }

    if ("invoicePaymentMethod" in payload && payload.invoicePaymentMethod) {
      form.invoicePaymentMethodId.onChange(payload.invoicePaymentMethod.id);
    }

    if ("proofOfPayments" in payload) {
      form.payment_receipt_ids.onChange(
        payload.proofOfPayments.map(({ id }) => id),
      );
    }

    if ("invoiceProofOfPayments" in payload) {
      form.payment_receipt_ids.onChange(
        payload.invoiceProofOfPayments?.map(({ invoice }) => invoice.id),
      );
    }

    payload.invoiceItems.forEach((i) => {
      form.addInvoiceItem({
        description: i.description ?? "",
        amount_in_currency: new NumberModel(
          forCreditNode ? -i.amount_in_currency : i.amount_in_currency,
          payload.currency.denomination,
        ),
        quantity: i.quantity.toString(),
        accounting_account_id: i.accounting_account_id ?? "",
        unit_price: nominate(
          i.unit_price ?? 100,
          this.appStore.workspaceStore.settings?.currency.denomination ?? 2,
        ).toString(),
        cost_center_id: i.accounting_centre_id ?? undefined,
        tag_ids: i.tag_ids ?? [],
        vat_percent_informative: i.vat_percent_informative?.toString(),
      });
    });

    if (
      payload.currency.id !== this.appStore.workspaceStore.settings?.currency.id
    ) {
      this.convertAmountUsingAPI({
        type: payload.workspace_exchange_rate_source,
        exchangeRate: payload.workspace_exchange_rate,
        currencyId: payload.currency.id,
        clientId:
          payload.workspace_exchange_rate_source === ExchangeRateSource.Client
            ? payload.client.id
            : undefined,
        date:
          "date_of_supply" in payload && payload.date_of_supply
            ? payload.date_of_supply
            : toApiDate(new Date()),
      });
    }
    const date =
      this.type === InvoiceTypeEnum.Proforma
        ? payload.issue_date
        : "date_of_supply" in payload
          ? payload.date_of_supply
          : undefined;

    this.fetchInvoiceNo(
      date,
      payload.workspace_company_id,
      this.invoiceSequenceType,
    );

    if ("invoice_no" in payload && "invoice_order_sequence_id" in payload) {
      this.handleSelectInvoiceNo(
        {
          value: payload.invoice_order_sequence_id,
          label: payload.invoice_no,
        },
        false,
      );
    }
  }

  @action.bound
  generateInvoiceItemsForTimeAndMaterials(option: GenerateFromOption) {
    this.form.resetInvoiceItems();
    const denomination =
      this.appStore.workspaceStore.settings?.currency.denomination ?? 2;

    switch (option) {
      case "budget-item":
        this.timeAndMaterialsStore.workTableState.generateInvoiceItems(
          option,
          this.form.addInvoiceItem,
          BudgetItemTypeEnum.OurWorkBudgetItem,
        );
        this.timeAndMaterialsStore.expenseTableState.generateInvoiceItems(
          option,
          this.form.addInvoiceItem,
          BudgetItemTypeEnum.ExpenseBudgetItem,
        );
        break;
      case "grouping":
        this.timeAndMaterialsStore.workTableState.generateInvoiceItems(
          option,
          this.form.addInvoiceItem,
          BudgetItemTypeEnum.OurWorkBudgetItem,
        );
        this.timeAndMaterialsStore.expenseTableState.generateInvoiceItems(
          option,
          this.form.addInvoiceItem,
          BudgetItemTypeEnum.ExpenseBudgetItem,
        );
        break;
      default:
        console.error(t`No generate from option passed.`);
        this.appStore.UIStore.toast({
          status: "error",
          title: t`Something unexpected happened, please try generating invoice items again.`,
        });
        break;
    }

    if (!this.form.invoice_items.$.length) {
      this.form.addInvoiceItem({
        description: "",
        amount_in_currency: new NumberModel(0, denomination),
        unit_price: "",
        accounting_account_id: "",
        quantity: "",
        cost_center_id: "",
        tag_ids: [],
        vat_percent_informative: this.form.vat_percent_informative.$ ?? "0",
      });
      this.appStore.UIStore.toast({
        title: t`There is no work or expenses to be invoiced since the last invoice`,
      });
    }

    if (this.convertedAmountRequired) {
      this.form.convertInvoiceItems();
    }
  }

  toggleCheckOurWorkItem = (
    budgetItem: TOurWorkBudgetItem,
    checked: boolean,
    budgetCategoryId: BudgetCategory["id"],
  ) => {
    if (checked) {
      if (this.form.our_work_budget_splits.$.get(budgetItem.id)) return;

      const amount = this.calcDefaultSplitAmount(budgetItem);
      const amountInPercent = this.calcAmountInPercent(
        amount,
        budgetItem.stats.total_budget,
      );
      this.form.addOurWorkBudgetSplit(
        budgetItem.id,
        new NumberModel(
          amount,
          this.appStore.workspaceStore.settings!.currency.denomination,
        ),
        budgetItem.budget_positions,
        budgetCategoryId,
        new NumberModel(amountInPercent, 2),
      );
    } else {
      this.form.our_work_budget_splits.$.delete(budgetItem.id);
    }
  };

  toggleCheckExpenseItem = (
    item: TExpenseBudgetItem,
    checked: boolean,
    budgetCategoryId: BudgetCategory["id"],
  ) => {
    if (checked) {
      if (this.form.expense_budget_splits.$.get(item.id)) return;

      const amount = this.calcDefaultSplitAmount(item);
      this.form.addExpenseBudgetSplit(
        item.id,
        new NumberModel(
          amount,
          this.appStore.workspaceStore.settings!.currency.denomination,
        ),
        budgetCategoryId,
      );
    } else {
      this.form.expense_budget_splits.$.delete(item.id);
    }
  };

  calcDefaultSplitAmount(
    item: TOurWorkBudgetItem | TExpenseBudgetItem,
  ): number {
    if (this.type === InvoiceTypeEnum.CreditNote) {
      return -item.stats.credit;
    } else {
      return item.stats.uninvoiced;
    }
  }

  calcAmountInPercent(amount: number, total_budget: number) {
    return (amount / total_budget) * HUNDRED_PERCENT;
  }
  private canUpdateBudgetItem(item: TOurWorkBudgetItem | TExpenseBudgetItem) {
    const credit = item?.stats.credit ?? 0;

    return this.isRFI || (this.isEdit && credit <= 0);
  }

  @computed
  get canApplyGlobalPercent(): boolean {
    let canUpdate = false;

    for (const key of this.form.our_work_budget_splits.$.keys()) {
      const item = this.ourWorkBudgetItems.find((item) => item.id === key);
      if (!item || !this.canUpdateBudgetItem(item)) continue;
      canUpdate = true;
      break;
    }

    for (const key of this.form.expense_budget_splits.$.keys()) {
      const item = this.expenseBudgetItems.find((item) => item.id === key);
      if (!item || !this.canUpdateBudgetItem(item)) continue;
      canUpdate = true;
      break;
    }

    return (
      !!this.appStore.workspaceStore.settings?.financial_amount_by_percent &&
      canUpdate
    );
  }

  @action.bound
  applyGlobalPercent(globalPercent: number) {
    globalPercent = Math.min(100, Math.max(0, globalPercent));
    const sign = this.type === InvoiceTypeEnum.CreditNote ? -1 : 1;

    this.form.our_work_budget_splits.$.forEach((split, id) => {
      const item = this.ourWorkBudgetItems.find((i) => i.id === id);
      const credit = item?.stats.credit ?? 0;
      const uninvoiced = item?.stats.uninvoiced ?? 0;

      if (!this.isRFI && this.isEdit && credit > 0) {
        return;
      }

      const amount = sign * (globalPercent / HUNDRED_PERCENT) * uninvoiced;
      NumberModel.setFieldState(split.$.amount, amount);
      NumberModel.setFieldState(split.$.amount_in_percent, globalPercent);
    });

    this.form.expense_budget_splits.$.forEach((split, id) => {
      const item = this.expenseBudgetItems.find((i) => i.id === id);
      if (
        !item ||
        item.stats.uninvoiced <= 0 ||
        (this.isEdit && item.stats.credit > 0)
      )
        return;

      const amount =
        sign * (globalPercent / HUNDRED_PERCENT) * item.stats.uninvoiced;
      NumberModel.setFieldState(split.$.amount, amount);
      NumberModel.setFieldState(split.$.amount_in_percent, globalPercent);
    });
  }

  showBudgetItem(item: TOurWorkBudgetItem | TExpenseBudgetItem): boolean {
    let show = true;

    if (this.hideAlreadyInvoiced && !this.hasBudgetItemAnyAmountToApply(item)) {
      show = false;
    }

    if (
      show &&
      this.showOnlyChecked &&
      !(isTOurWorkBudgetItem(item)
        ? this.form.our_work_budget_splits.$.get(item.id)
        : this.form.expense_budget_splits.$.get(item.id))
    ) {
      show = false;
    }

    return show;
  }

  hasBudgetItemAnyAmountToApply(
    item: TOurWorkBudgetItem | TExpenseBudgetItem,
  ): boolean {
    if (this.type === InvoiceTypeEnum.CreditNote) {
      return item.stats.credit !== 0;
    }

    return item.stats.uninvoiced !== 0;
  }

  setClient = (brandIds: string[]) => {
    if (!brandIds.length) {
      this.form.client_id.reset("");
      this.fetchBillingEntities("");
    } else {
      const clientId = this.clientsSimple.find((i) =>
        i.brands.find((i) => i.id === brandIds[0]),
      )?.id;
      this.form.client_id.reset(clientId || "");
      this.fetchBillingEntities(clientId || "");
    }
  };

  setTeamFromProjects = () => {
    if (this.isRFI && !this.isFinancier) {
      const teamId = this.appStore.authStore.user?.team?.id;
      this.form.team_ids.onChange(teamId ? [teamId] : []);
      return;
    }

    const projectIds = this.form.project_ids.value;
    if (!projectIds.length) {
      this.form.team_ids.reset([]);
      return;
    }

    const projectsSimple = this.projectsSimple.filter(({ id }) =>
      projectIds.includes(id),
    );
    if (projectsSimple) {
      const uniqueTeamIds: string[] = [];

      new Set(projectsSimple.map((project) => project.team_id)).forEach(
        (teamId) => {
          uniqueTeamIds.push(teamId);
        },
      );

      this.form.team_ids.reset(uniqueTeamIds);
    } else {
      this.form.team_ids.reset([]);
    }
  };

  resetOnSelectBrands = () => {
    if (this.isBrandMultiSelect && this.form.brand_ids.value.length) {
      this.filterSelectedContact();
      this.filterSelectedProjects();
      this.filterSelectedOrders();
      this.filterSelectedProformaInvoices();
      this.filterSelectedPrepayments();
    } else {
      this.form.contact_id.reset();
      this.form.project_ids.reset();
      this.form.order_ids.reset();
      this.form.billing_entity_id.reset();
      this.form.vat_percent_informative.reset();
      this.form.vat_reverse_charge.reset();
      this.form.outgoing_proforma_invoice_ids.reset();
      this.form.resetBudgetSplits();
      this.form.resetPrepaymentSplits();
    }
    this.form.workspace_company_id.reset();
    this.form.media_ids.reset();
  };

  resetOnSelectProjects = () => {
    this.filterSelectedOrders();
    this.filterSelectedProformaInvoices();
    this.form.media_ids.reset();
  };

  filterSelectedContact = () => {
    const contact = this.selectedBrands.find((i) =>
      i.contacts.find((i) => i.id === this.form.contact_id.value),
    );
    contact || this.form.contact_id.reset();
  };

  filterSelectedProjects = () => {
    const availableProjectIds = this.projectsSimple.map((i) => i.id);
    this.form.project_ids.reset(
      this.form.project_ids.value.filter((id) =>
        availableProjectIds.includes(id),
      ),
    );
  };

  filterSelectedOrders = () => {
    const availableOrderIds = this.selectedProjects
      .filter((i) => this.form.project_ids.value.includes(i.id))
      .flatMap((i) => i.ordersSimpleMap.map((j) => j.id));
    this.form.order_ids.onChange(
      this.form.order_ids.value.filter((i) => availableOrderIds.includes(i)),
    );
  };

  filterSelectedProformaInvoices = () => {
    const availableProformaIds = this.proformaInvoices
      .filter((i) =>
        i.projects.find((j) => this.form.project_ids.value.includes(j.id)),
      )
      .map((i) => i.id);
    this.form.outgoing_proforma_invoice_ids.onChange(
      this.form.outgoing_proforma_invoice_ids.value.filter((i) =>
        availableProformaIds.includes(i),
      ),
    );
  };

  filterSelectedPrepayments = () => {
    const creditAccountsOfSelectedBrands = this.selectedBrands.flatMap((i) =>
      (i.creditAccounts || []).map((j) => j.id),
    );
    this.form.prepayment_splits.$.forEach((i, idx) => {
      if (i.$.brand_credit_account_id.value) {
        creditAccountsOfSelectedBrands.includes(
          i.$.brand_credit_account_id.value,
        ) || this.form.removePrepaymentSplit(idx);
      }
    });
  };

  /**
   * Filter selected budgetSplits by selected projects.
   *
   * It has to be called after fetching projects, not
   * after selecting.
   * */
  filterSelectedSplits = () => {
    const availableOurWorkIDs = this.selectedProjects.flatMap((i) =>
      i.ourWorkStructured.flatMap((j) => j.budgetItems.map((k) => k.id)),
    );

    this.form.our_work_budget_splits.$.forEach((_, id) => {
      if (!availableOurWorkIDs.includes(id)) {
        this.form.our_work_budget_splits.$.delete(id);
      }
    });

    const availableExpenseIDs = this.selectedProjects.flatMap((i) =>
      i.expensesWithoutOtherStructured.flatMap((j) =>
        j.budgetItems.map((k) => k.id),
      ),
    );

    this.form.expense_budget_splits.$.forEach((_, id) => {
      if (!availableExpenseIDs.includes(id)) {
        this.form.expense_budget_splits.$.delete(id);
      }
    });
  };

  presetBillingEntity = () => {
    if (
      !this.form.billing_entity_id.value &&
      this.selectedBrands.length &&
      this.selectedBrands[0].defaultBillingEntity?.id
    ) {
      this.form.billing_entity_id.onChange(
        this.selectedBrands[0].defaultBillingEntity?.id,
      );

      if (this.selectedBillingEntity?.payment_due) {
        this.form.due_date.onChange(
          addDays(new Date(), this.selectedBillingEntity.payment_due),
        );
      }
      if (this.selectedBillingEntity?.defaultCurrency.id) {
        this.form.currency_id.onChange(
          this.selectedBillingEntity.defaultCurrency.id,
        );
      }
      this.setVatPercentInformativeByBillingEntity();
    } else {
      this.setVatPercentInformativeByBillingEntity(true);
    }

    if (this.formMode !== FormMode.EDIT) {
      this.syncVatReverseChargeWithCountries();
    }
  };

  presetContact = () => {
    const projectCount = this.selectedProjects.length;
    const firstProject =
      projectCount > 0 ? this.selectedProjects[0] : undefined;

    if (this.selectedBrands.length && firstProject?.contact?.id) {
      this.form.contact_id.onChange(firstProject.contact.id);
    }
  };

  presetTeam = () => {
    if (this.form.team_ids.value) return;

    if (this.teamSimple.length === 1) {
      this.form.team_ids.reset([this.teamSimple[0].id]);
    }
  };

  setVatPercentInformativeByBillingEntity = (initialSet = false) => {
    if (
      this.hasDifferentBillingAndLegalEntityCountry &&
      this.form.vat_reverse_charge.value
    ) {
      this.form.vat_percent_informative.onChange("0");
      return;
    }

    let vatPercent = "0";
    const billingEntity = this.selectedBillingEntity;

    if (billingEntity?.applies_vat) {
      vatPercent =
        this.isEdit &&
        initialSet &&
        typeof this.invoice?.vat_percent_informative === "number"
          ? this.invoice.vat_percent_informative === 0
            ? this.appStore.workspaceStore.settings!.defaultVatRate.amount.toString()
            : this.invoice?.vat_percent_informative?.toString()
          : this.appStore.workspaceStore.settings!.defaultVatRate.amount.toString();
    }

    this.form.vat_percent_informative.onChange(vatPercent);
  };

  @action.bound
  syncVatReverseChargeWithCountries({
    defaultWhenDifferent = true,
    applyDefaultVatWhenSame = true,
  }: {
    defaultWhenDifferent?: boolean;
    applyDefaultVatWhenSame?: boolean;
  } = {}) {
    if (!this.selectedBillingEntity) return;

    if (!this.hasDifferentBillingAndLegalEntityCountry) {
      if (this.form.vat_reverse_charge.value) {
        this.form.vat_reverse_charge.onChange(false);
      }
      if (applyDefaultVatWhenSame) {
        this.setVatPercentInformativeByBillingEntity();
      }
      return;
    }

    if (defaultWhenDifferent) {
      this.form.vat_reverse_charge.onChange(true);
    }

    if (this.form.vat_reverse_charge.value) {
      this.form.vat_percent_informative.onChange("0");
    }
  }

  @action.bound
  handleVatReverseChargeChange(value: boolean) {
    this.form.vat_reverse_charge.onChange(value);

    if (value) {
      this.form.vat_percent_informative.onChange("0");
    }
  }

  presetOwner = () => {
    // if no project & owner fill auth user as owner (https://brackets.atlassian.net/browse/ALLF-3062?atlOrigin=eyJpIjoiMzczZmNlZTIwOWYwNDY0MmI2YTFkZmQ0MGY2MTlkYzYiLCJwIjoiaiJ9)
    if (!this.form.owner_id.value && !this.selectedProjects.length) {
      const authUser = this.appStore.authStore.user!.id;
      this.form.owner_id.onChange(authUser);
    }
    if (this.form.owner_id.value || !this.selectedProjects.length) return;

    const projectManagerId = this.selectedProjects[0].projectManager.id;
    this.form.owner_id.onChange(projectManagerId);
  };

  createRequestForInvoiceInput = (
    status: RequestForInvoicingStatusEnum,
  ): CreateRequestForInvoicingInput => {
    return {
      type: InvoiceTypeEnumToRfiEnum(this.type),
      status,
      ...omit(
        this.form.data,
        "invoice_no",
        "invoice_order_sequence_id",
        "refresh_billing_entity",
      ),
      show_unit_price: this.showQuantity.value,
      show_billing_accounting: this.showAccountClassification.value,
      timeMaterialsInvoiceData: this.isTimeAndMaterialsSelected
        ? this.timeAndMaterialsStore.timeMaterialsInvoiceData
        : undefined,
    };
  };

  updateRequestForInvoiceInput = (
    status: RequestForInvoicingStatusEnum,
  ): UpdateRequestForInvoicingInput => {
    const fullFormData = this.createRequestForInvoiceInput(status);
    const isRfiEdit = this.formMode === FormMode.EDIT && this.isRFI;
    const formData = isRfiEdit
      ? omit(
          fullFormData,
          "type",
          "invoice_no",
          "invoice_order_sequence_id",
          "refresh_billing_entity",
        )
      : omit(fullFormData, "type");

    return {
      id: this.invoice_id!,
      ...formData,
    };
  };

  updateOutgoingInvoiceInput = (): UpdateOutgoingInvoiceInput => {
    const { due_date, issue_date, date_of_supply, invoice_no, ...restData } =
      this.form.data;

    return {
      id: this.invoice_id!,
      due_date: due_date!,
      issue_date: issue_date!,
      date_of_supply: date_of_supply!,
      show_unit_price: this.showQuantity.value,
      show_billing_accounting: this.showAccountClassification.value,
      timeMaterialsInvoiceData: this.isTimeAndMaterialsSelected
        ? this.timeAndMaterialsStore.timeMaterialsInvoiceData
        : undefined,
      invoice_no: invoice_no ?? "",
      ...restData,
      refresh_billing_entity: this.isEdit
        ? this.form.refresh_billing_entity.value
        : undefined,
    };
  };

  updateOutgoingProformaInvoiceInput =
    (): UpdateOutgoingProformaInvoiceInput => {
      const {
        due_date,
        issue_date,
        team_ids,
        date_of_supply,
        invoice_no,
        ...restData
      } = this.form.data;

      return {
        id: this.invoice_id!,
        due_date: due_date!,
        issue_date: issue_date!,
        show_unit_price: this.showQuantity.value,
        show_billing_accounting: this.showAccountClassification.value,
        team_ids: team_ids,
        invoice_no: invoice_no ?? "",
        ...omit(
          restData,
          "client_id",
          "brand_ids",
          "order_ids",
          "outgoing_proforma_invoice_ids",
          "proof_of_payments_ids",
        ),
        refresh_billing_entity: this.isEdit
          ? this.form.refresh_billing_entity.value
          : undefined,
      };
    };

  getOIPrepaymentSplitBy(brand_credit_account_id: string) {
    if (!this.invoice || this.isRFI || this.type !== InvoiceTypeEnum.Outgoing)
      return undefined;

    if ("prepaymentSplits" in this.invoice && this.invoice.prepaymentSplits) {
      for (const pps of this.invoice.prepaymentSplits) {
        if (
          pps.brand_credit_account_id === brand_credit_account_id &&
          "amount_left" in pps
        ) {
          return pps;
        }
      }
    }

    return undefined;
  }

  @action.bound
  async getExchangeRate() {
    if (!this.needsExchangeRate) return;
    if (this.form.exchange_rate_source.value === ExchangeRateSource.Custom)
      return;

    const projectId = this.form.project_ids.$?.[0];
    const clientId =
      this.form.client_id.$.length > 0 ? this.form.client_id.$ : undefined;

    if (!this.canFetchExchangeRate(projectId, clientId)) return;

    const [data, error] = await this.exchangeRateFetcher.fetch(
      {
        date: toApiDate(this.form.date_of_supply.value ?? new Date()),
        type: this.form.exchange_rate_source.value,
        currencyId: this.selectedCurrency.id,
        clientId,
        projectId,
        workspaceCompanyId: this.form.workspace_company_id.$ || undefined,
      },
      null,
    );

    this.form.handleExchangeRateChange(
      error || !data.convertAmountToWorkspaceCurrencyUsingExchangeRate
        ? "1"
        : nominate(
            data.convertAmountToWorkspaceCurrencyUsingExchangeRate
              ?.exchange_rate,
            EXCHANGE_RATE_DENOMINATION,
          ).toString(),
    );

    this.convertAmountUsingAPI();
  }

  @computed
  get isWorkspaceExchangeRateLoading(): boolean {
    return this.workspaceExchangeRateFetcher.isLoading.value;
  }

  @action.bound
  async getWorkspaceExchangeRate() {
    if (
      this.selectedCurrency.id ===
      this.appStore.workspaceStore.settings?.currency.id
    ) {
      this.form.handleChangeWorkspaceExchangeRate("1");
      return;
    }

    const projectId = this.form.project_ids.$?.[0];
    const clientId =
      this.form.client_id.$.length > 0 ? this.form.client_id.$ : undefined;

    if (
      !this.canFetchExchangeRate(
        projectId,
        clientId,
        this.form.workspace_exchange_rate_source.value,
      )
    )
      return;

    const [data, error] = await this.workspaceExchangeRateFetcher.fetch(
      {
        date: toApiDate(this.form.date_of_supply.value ?? new Date()),
        type: this.form.workspace_exchange_rate_source.value,
        currencyId: this.selectedCurrency.id,
        clientId,
        projectId,
        workspaceCompanyId: this.form.workspace_company_id.$ || undefined,
      },
      null,
    );

    if (error || !data.convertAmountToWorkspaceCurrencyUsingExchangeRate) {
      this.form.handleChangeWorkspaceExchangeRate("1");
      return;
    }

    const newExchangeRate = nominate(
      data.convertAmountToWorkspaceCurrencyUsingExchangeRate.exchange_rate,
      EXCHANGE_RATE_DENOMINATION,
    );

    this.form.handleChangeWorkspaceExchangeRate(newExchangeRate.toString());
  }

  async fetchExchangeRates() {
    this.getExchangeRate();
    this.getWorkspaceExchangeRate();
  }

  @action.bound async convertAmountUsingAPI(
    variables?: Partial<ConvertAmountQueryVariables>,
  ) {
    if (!this.needsExchangeRate) return;
    const type = variables?.type ?? this.form.workspace_exchange_rate_source.$;
    const projectId = variables?.projectId ?? this.form.project_ids.$?.[0];
    const clientId =
      type === ExchangeRateSource.Client
        ? (variables?.clientId ?? this.form.client_id.$)
        : undefined;
    const exchangeRateSource =
      type === ExchangeRateSource.Custom
        ? (variables?.exchangeRate ??
          this.form.workspace_exchange_rate.$.denominatedValue)
        : undefined;

    if (!this.canFetchExchangeRate(projectId, clientId, type)) {
      this.setConvertedAmount(undefined);
      return;
    }

    const [data, error] = await this.convertAmountFetcher.fetch({
      amount: variables?.amount ?? this.sumInvoiceItems,
      clientId,
      projectId,
      workspaceCompanyId:
        variables?.workspaceCompanyId ??
        (this.form.workspace_company_id.$ || undefined),
      currencyId: variables?.currencyId ?? this.selectedCurrency.id,
      exchangeRate: exchangeRateSource,
      type: type,
      date:
        variables?.date ?? toApiDate(this.form.date_of_supply.$ ?? new Date()),
    });

    if (error) {
      this.setConvertedAmount(undefined);
      return;
    }

    this.setConvertedAmount(
      data?.convertAmountToWorkspaceCurrencyUsingExchangeRate?.amount ??
        undefined,
    );
  }

  @computed
  get canFetchExchangeRate() {
    return (
      projectId?: string,
      clientId?: string,
      exchangeRateSource?: ExchangeRateSource,
    ) => {
      exchangeRateSource ??=
        this.form.exchange_rate_source.value ??
        this.form.workspace_exchange_rate_source.value;

      if (!projectId && exchangeRateSource === ExchangeRateSource.Budget) {
        // this.appStore.UIStore.toast({
        //   status: "error",
        //   title: t`Project is not selected`,
        //   description: t`The project selection is mandatory when the exchange rate originates from the Budget setting.`,
        // });

        return false;
      }

      if (!clientId && exchangeRateSource === ExchangeRateSource.Client) {
        // this.appStore.UIStore.toast({
        //   status: "error",
        //   title: t`Client is not selected`,
        //   description: t`The client selection is mandatory when the exchange rate originates from the Client setting.`,
        // });

        return false;
      }

      return true;
    };
  }

  @action.bound
  handleDateOfSupplyChange(range: DateRange | undefined) {
    this.form.date_of_supply.onChange(range?.start);
    this.fetchExchangeRates();
    this.form.issue_date.validate();
    this.form.due_date.validate();
    if (!this.isRFI) return;
    this.form.invoice_no.reset();
    this.form.invoice_order_sequence_id.reset();
    this.fetchInvoiceNo(
      range?.start,
      this.form.workspace_company_id.value,
      this.invoiceSequenceType,
    );
  }

  @computed get showBankAccountSelect() {
    return (
      appStore.workspaceStore.companyHasMultipleBankAccounts(
        this.form.workspace_company_id.$,
        this.form.workspace_bank_account_id.$,
      ) || this.form.workspace_bank_account_id.hasError
    );
  }

  @computed get selectedDifferentWorkspaceCompany(): boolean {
    if (!this.invoice) return true;
    if (!this.invoice_no) return true;
    return (
      this.invoice.workspace_company_id !== this.form.workspace_company_id.value
    );
  }

  @action.bound async fetchProjectsData(
    skipTimeAndMaterialsInvoiceDataFetch?: boolean,
  ) {
    this.setSelectedProjectsLoading(true);
    const { data, error } = await client.query<
      ProjectsDataQuery,
      ProjectsDataQueryVariables
    >({
      query: ProjectsDataDocument,
      variables: {
        project_ids: this.form.project_ids.value,
        projectsCount: this.form.project_ids.value.length + 1,
      },
    });
    this.setSelectedProjectsLoading(false);

    if (!data || error) return;

    this.setSelectedProjects(data, skipTimeAndMaterialsInvoiceDataFetch);
  }

  @action.bound resetOrderOptions() {
    this.orderOptions = [];
    this.form.order_ids.reset([]);
  }

  @action.bound setOrdersBrandFilter(brandIds: string[]) {
    this.ordersWhere.filtersByColumn
      .get(OrdersWhereColumn.BrandId)
      ?.setValue(brandIds);
    this.resetOrderOptions();
  }

  @action.bound setOrdersProjectFilter(projectIds: string[]) {
    this.ordersWhere.filtersByColumn
      .get(OrdersWhereColumn.ProjectId)
      ?.setValue(projectIds);
    this.resetOrderOptions();
  }

  @action.bound async fetchOrders(
    page: number,
    perPage: number,
    searchPhrase: string,
  ): Promise<{
    data: IOption[];
    paginatorInfo: PaginatorInfo | undefined;
  }> {
    const [data, error] = await this.orderFetcher.fetch({
      page: page,
      first: perPage,
      filters: {
        search: searchPhrase,
        where: this.ordersWhere.asGraphQueryParam,
      },
    });

    if (error || !data.orders)
      return {
        data: [],
        paginatorInfo: undefined,
      };

    return {
      data: data.orders.data.map((order) => ({
        value: order.id,
        label: order.purchase_order_no ?? "",
      })),
      paginatorInfo: data.orders.paginatorInfo,
    };
  }

  @action.bound addOrderOption(
    order: Pick<CreateOrderMutation["createOrder"], "id" | "purchase_order_no">,
  ) {
    this.orderOptions.push({
      value: order.id,
      label: order.purchase_order_no ?? "",
    });
  }

  @computed get tagOptions() {
    return toTagOptions(this.appStore.tagStore.tags);
  }

  @computed
  get isOutgoingInvoicesLoading(): boolean {
    return this.outgoingInvoicesFetcher.isLoading.value;
  }

  @action.bound
  async fetchOutgoingInvoices(
    page: number,
    perPage: number,
    searchPhrase: string,
  ): Promise<{ data: IOption[]; paginatorInfo: PaginatorInfo | undefined }> {
    const [data, error] = await this.outgoingInvoicesFetcher.fetch({
      page,
      first: perPage,
      filters: {
        search: searchPhrase,
      },
    });

    if (error || !data.outgoingInvoices) {
      return {
        data: [],
        paginatorInfo: undefined,
      };
    }

    return {
      data: data.outgoingInvoices.data.map((invoice) => ({
        value: invoice.id,
        label: invoice.invoice_no ?? "",
      })),
      paginatorInfo: data.outgoingInvoices?.paginatorInfo,
    };
  }

  @computed
  get isOutgoingInvoiceDetailLoading(): boolean {
    return this.outgoingInvoiceDetailFetcher.isLoading.value;
  }

  @action.bound
  private async fetchOriginalInvoiceOption(
    id: string,
  ): Promise<OutgoingInvoiceQuery | null> {
    const [data, error] = await this.outgoingInvoiceDetailFetcher.fetch({
      id,
    });

    if (error || !data.outgoingInvoice) {
      this.originalInvoiceOption = undefined;
      return null;
    }

    this.originalInvoiceOption = [
      {
        value: data.outgoingInvoice.id,
        label: data.outgoingInvoice.invoice_no,
      },
    ];

    return data;
  }

  @action.bound
  async handleSelectOutgoingInvoice(id: string): Promise<void> {
    this.form.credit_note_outgoing_invoice_id.onChange(id);
    const data = await this.fetchOriginalInvoiceOption(id);

    if (!data?.outgoingInvoice) return;

    await this.fillForm(
      data.outgoingInvoice,
      this.type === InvoiceTypeEnum.CreditNote,
    );
    // NOTE: We need to set this again in case fetched outgoing invoice has already set credit_note_outgoing_invoice_id
    this.form.credit_note_outgoing_invoice_id.onChange(id);
    // NOTE: We have to reset these fields because they are set in fillForm and this is new invoice so we can't use existing invoice_no
    this.form.invoice_no.reset("");
    this.form.invoice_no.error = undefined;
    this.form.invoice_order_sequence_id.reset("");
    this.form.invoice_order_sequence_id.error = undefined;
    this.presetTimeAndMaterials(data.outgoingInvoice, "oi");
  }

  @action.bound
  handleOutgoingProformaInvoiceIdsChange(value: string[]) {
    this.form.outgoing_proforma_invoice_ids.onChange(value);
    this.form.payment_receipt_ids.reset([]);
  }

  @computed
  get paymentReceipts(): ProofOfPaymentFragment[] {
    if (!this.form.outgoing_proforma_invoice_ids.value) return [];
    return this.proformaInvoices
      .filter(({ id }) =>
        this.form.outgoing_proforma_invoice_ids.value.includes(id),
      )
      .flatMap(({ proofOfPayments }) => compact(proofOfPayments))
      .filter(
        ({ summaryInvoicesForProofOfPayment }) =>
          !summaryInvoicesForProofOfPayment?.length ||
          (this.invoice?.id &&
            summaryInvoicesForProofOfPayment.some(
              ({ id }) => id === this.invoice?.id,
            )),
      );
  }

  @computed
  get paymentReceiptOptions(): IOption[] {
    return this.paymentReceipts.map(({ id, invoice_no, stats }) => {
      const totalAmount = currency.formatByWorkspace(
        stats.total_amount_in_currency,
      );
      const totalAmountWithVat = currency.formatByWorkspace(
        stats.total_amount_with_vat_in_currency,
      );

      return {
        value: id,
        label: `${invoice_no} (${totalAmount}/${totalAmountWithVat} ${t`with VAT`})`,
      };
    });
  }

  @computed
  get getModelLockType() {
    switch (this.type) {
      case InvoiceTypeEnum.Outgoing:
        return ModelLockLockableTypeEnum.OutgoingInvoice;
      case InvoiceTypeEnum.Proforma:
        return ModelLockLockableTypeEnum.OutgoingProformaInvoice;
      default:
        return null;
    }
  }

  @computed
  get isLocked(): boolean {
    if (!this.invoice || !("modelLock" in this.invoice)) {
      return false;
    }
    return this.invoice.modelLock?.is_locked ?? false;
  }

  @computed
  get isUnlocking(): boolean {
    if (!this.invoice || !this.getModelLockType) {
      return false;
    }

    return this.appStore.modelLock.isUnlocking(
      this.invoice.id,
      this.getModelLockType,
    );
  }

  @action.bound
  async unlock(): Promise<boolean> {
    if (!this.invoice?.id || !this.getModelLockType) return false;

    const [data, error] = await this.appStore.modelLock.unlockModel(
      this.invoice.id,
      this.getModelLockType,
    );

    if (error || !data?.unlockModel) return false;

    this.appStore.UIStore.toast({
      status: "success",
      title:
        this.type === InvoiceTypeEnum.Proforma
          ? t`Proforma invoice unlocked successfully`
          : this.type === InvoiceTypeEnum.Outgoing
            ? t`Outgoing invoice unlocked successfully`
            : t`Invoice unlocked successfully`,
    });

    if ("modelLock" in this.invoice) {
      this.invoice = { ...this.invoice, modelLock: null };
    }

    return true;
  }
}
